import { productLog } from "common/utils/logging";
import perfNow from "common/utils/perfNow";
import { genMonochromeNoiseBitmap } from "common/utils/random";
import type { PixelFormat } from "three";
import {
  DataTexture,
  LinearFilter,
  LinearMipmapLinearFilter,
  RGBAFormat,
  RedFormat,
  RepeatWrapping,
  UVMapping,
  UnsignedByteType,
} from "three";
import { HAVE_IMAGEBITMAP } from "../constants";
import type { LoadTextureFunc } from "./loadTextureFuncType";
import { urlFromTextureName } from "./materialEditorTextures";

export const cachedProceduralTextures: { [name: string]: DataTexture } = {};

export function getLoadTextureFunc(
  loadTextureUrl: LoadTextureFunc,
  lossyWebPSupported: boolean,
  useWebgl2TextureAtlas: boolean,
): LoadTextureFunc {
  return (name: string, overrideFormat: PixelFormat | undefined) => {
    // We can generate some textures procedurally here instead of loading them with the URL.
    if (name in cachedProceduralTextures) {
      return [cachedProceduralTextures[name], Promise.resolve()];
    }
    const proceduralTextureWidth = 1024;
    if (name === "DV_Grunge_Noise_White") {
      const t0 = perfNow();
      if (!(overrideFormat === RGBAFormat || overrideFormat === RedFormat)) {
        console.assert(false);
      }
      let channels = 1;
      if (overrideFormat === RGBAFormat) {
        channels = 4;
      }
      const flipY = useWebgl2TextureAtlas && HAVE_IMAGEBITMAP;
      const buffer = genMonochromeNoiseBitmap(channels, proceduralTextureWidth, 0, flipY);

      const t1 = perfNow();
      if (t1 - t0 > 100) {
        productLog("Generated white noise texture in " + (t1 - t0) + " ms");
      }

      const dataTexture = new DataTexture(
        buffer,
        proceduralTextureWidth,
        proceduralTextureWidth,
        overrideFormat,
        UnsignedByteType,
        UVMapping,
        RepeatWrapping,
        RepeatWrapping,
        LinearFilter,
        LinearMipmapLinearFilter,
      );
      dataTexture.generateMipmaps = true;
      dataTexture.needsUpdate = true;
      cachedProceduralTextures[name] = dataTexture;
      return [dataTexture, Promise.resolve()];
    }
    return loadTextureUrl(urlFromTextureName(name, lossyWebPSupported), overrideFormat);
  };
}
